// src/pages/Admin/AdminUserManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Stack,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Search as SearchIcon, CheckCircle as CheckCircleIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../services/firebase'; // Adjust path if needed
import { 
  auth, 
  db, 
  functions, 
  analytics, 
  logEvent 
} from '../../services/firebase';  // ← Adjust path to match your actual file location

// const auth = getAuth(app);
// const db = getFirestore(app);
// const functions = getFunctions(app);
// Now just use auth, db, functions directly
// Example:
//                const userDocRef = doc(db, 'users', someUid);

function AdminUserManagement() {
  const [searchEmail, setSearchEmail] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [admins, setAdmins] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [demoteDialogOpen, setDemoteDialogOpen] = useState(false);
  const [userToDemote, setUserToDemote] = useState(null);
  const [roleToDemote, setRoleToDemote] = useState(null);
  // const fetchAdminsOLD = useCallback(async () => {
  //   setIsListLoading(true);
  //   try {
  //     const adminsCol = collection(db, 'admins');
  //     const adminSnapshot = await getDocs(adminsCol);
  //     const adminList = adminSnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
      
  //     // To get email/displayName, we'd need another function or store it here.
  //     // For now, let's just display what we have. A more advanced version could
  //     // use a Cloud Function to join this data.
  //     setAdmins(adminList);
  //   } catch (err) {
  //     setError('Failed to load the list of admins.');
  //     console.error(err);
  //   } finally {
  //     setIsListLoading(false);
  //   }
  // }, []);

  const fetchAuthors = useCallback(async () => {
    try {
      const getAuthors = httpsCallable(functions, 'getAuthorsList');
      const result = await getAuthors();
      setAuthors(result.data.authors || []);
    } catch (err) {
      setError('Failed to load authors list');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);


  const fetchAdmins = useCallback(async () => {
  try {
    const getAdmins = httpsCallable(functions, 'getAdminsList');
    const result = await getAdmins();
    setAdmins(result.data.admins || []);
  } catch (err) {
    setError('Failed to load admins list');
    console.error(err);
  } finally {
    setIsListLoading(false);
  }
}, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleSearch = async () => {
    const trimmedEmail = searchEmail.trim();
    if (!trimmedEmail) {
      setError('Please enter an email address to search.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setFoundUser(null);

    try {
      // This is the correct implementation: calling the secure Cloud Function.
      const lookupUser = httpsCallable(functions, 'lookupUserByEmail');
      const result = await lookupUser({ email: trimmedEmail });
      const user = result.data;

      if (user) {
        // The callable function returns the user object. `localId` is the `uid`.
        setFoundUser({ ...user, localId: user.uid });
      } else {
        setError('User not found');
      }
    } catch (err) {
      // The callable function provides structured, meaningful error messages.
      setError(`Search failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePromote = async (uid) => {
    try {
      const promoteUser = httpsCallable(functions, 'promoteUserToAdmin');
      await promoteUser({ uid: uid });

      setFoundUser(null);
      setError('');
      setSearchEmail('');
      setSuccess('User promoted to admin successfully!');
      fetchAdmins(); // Re-fetch the list to show the new admin
    } catch (err) {
      setSuccess('');
      setError(`Failed to promote user: ${err.message}`);
    }
  };

  const handlePromoteToAuthor = async (uid) => {
    try {
      const promoteUser = httpsCallable(functions, 'promoteUserToAuthor');
      await promoteUser({ uid: uid });

      setFoundUser(null);
      setError('');
      setSearchEmail('');
      setSuccess('User promoted to author successfully!');
      fetchAuthors(); // Re-fetch the list to show the new author
    } catch (err) {
      setSuccess('');
      setError(`Failed to promote user: ${err.message}`);
    }
  };

  const handleDemoteAdmin = async (uid) => {
    try {
      const demoteUser = httpsCallable(functions, 'demoteUserFromAdmin');
      await demoteUser({ uid });
      setSuccess('User demoted successfully!');
      setError('');
      fetchAdmins(); // Refresh the list
    } catch (err) {
      setError(`Failed to demote user: ${err.message}`);
      setSuccess('');
    }
  };

  const handleDemoteAuthor = async (uid) => {
    try {
      const demoteUser = httpsCallable(functions, 'demoteUserFromAuthor');
      await demoteUser({ uid });
      setSuccess('User demoted successfully!');
      setError('');
      fetchAuthors(); // Refresh the list
    } catch (err) {
      setError(`Failed to demote user: ${err.message}`);
      setSuccess('');
    }
  };

  const openDemoteDialog = (user, role) => {
    setUserToDemote(user);
    setRoleToDemote(role);
    setDemoteDialogOpen(true);
  };

  const closeDemoteDialog = () => {
    setUserToDemote(null);
    setRoleToDemote(null);
    setDemoteDialogOpen(false);
  };

  const confirmDemote = () => {
    if (userToDemote) {
      if (roleToDemote === 'admin') {
        handleDemoteAdmin(userToDemote.uid);
      } else if (roleToDemote === 'author') {
        handleDemoteAuthor(userToDemote.uid);
      }
    }
    closeDemoteDialog();
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      <Box display="flex" mb={3}>
        <TextField
          label="Search by Email"
          variant="outlined"    
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} disabled={loading}>
                  <SearchIcon />
                </IconButton>   
                </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Typography color="error" gutterBottom>
          {error}
        </Typography>
      )}

      {success && (
        <Typography color="success.main" gutterBottom>
          {success}
        </Typography>
      )}

      {foundUser && (
        <Box
          mb={3}
          p={2}
          border={1}
          borderColor="grey.300"
          borderRadius={4}
          position="relative"
        >
          <IconButton
            aria-label="clear search result"
            onClick={() => setFoundUser(null)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6">User Found:</Typography>
          <Typography>Email: {foundUser.email}</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={() => handlePromote(foundUser.localId)}
            >
              Promote to Admin
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handlePromoteToAuthor(foundUser.localId)}
            >
              Promote to Author
            </Button>
          </Stack>
        </Box>
      )}

      <Typography variant="h5" gutterBottom>
        Current Admins
      </Typography>
      <TableContainer component={Paper}>
        <Table size="medium" aria-label="simple table">
          <TableHead>
            <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Promoted At</TableCell>
                <TableCell>UID</TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isListLoading ? (
              <TableRow><TableCell colSpan={4} align="center">Loading admins...</TableCell></TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No admins yet.
                </TableCell>
              </TableRow>
            ) : (
            admins.map((admin) => (
              <TableRow sx={{ bgcolor: '#f5f5f5' }} key={admin.uid}>
                  <TableCell>{admin.email || 'N/A'}</TableCell>
                  <TableCell>{admin.promotedAt ? new Date(admin.promotedAt.seconds * 1000).toLocaleString() : 'N/A'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {admin.uid}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => openDemoteDialog(admin, 'admin')}
                    >
                      Demote
                    </Button>
                  </TableCell>
              </TableRow>
            ))
        )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Current Authors
      </Typography>
      <TableContainer component={Paper}>
        <Table size="medium" aria-label="simple table">
          <TableHead>
            <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Promoted At</TableCell>
                <TableCell>UID</TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isListLoading ? (
              <TableRow><TableCell colSpan={4} align="center">Loading authors...</TableCell></TableRow>
            ) : authors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No authors yet.
                </TableCell>
              </TableRow>
            ) : (
            authors.map((author) => (
              <TableRow sx={{ bgcolor: '#f5f5f5' }} key={author.uid}>
                  <TableCell>{author.email || 'N/A'}</TableCell>
                  <TableCell>{author.promotedAt ? new Date(author.promotedAt.seconds * 1000).toLocaleString() : 'N/A'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {author.uid}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => openDemoteDialog(author, 'author')}
                    >
                      Demote
                    </Button>
                  </TableCell>
              </TableRow>
            ))
        )}
          </TableBody>
        </Table>
      </TableContainer>


      <Dialog open={demoteDialogOpen} onClose={closeDemoteDialog}>
        <DialogTitle>Confirm Demotion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to demote this user? They will lose all{' '}
            {roleToDemote} privileges.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDemoteDialog}>Cancel</Button>
          <Button onClick={confirmDemote} color="error">
            Demote
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminUserManagement;